import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo } from 'react';
import { useAuth } from '@/auth/useAuth';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { pipelinesGridConfig } from '@/features/pipelines/grid/gridConfig';
import {
  useProjectFilter,
  useProjectMetricThresholds,
  useProjectsQuery,
} from '@/features/projects';
import { Stack } from '@/ui';
import { buildTableGridProps } from '@/ui/composites/grid/tableGridProps';
import { ServerDataTable } from '@/ui/composites/ServerDataTable';
import { type GridFilterModel, type GridSortModel } from '@/ui/primitives';
import { buildPipelinesColumns } from './pipelinesColumns';
import type { GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type {
  BuildStatus,
  MetricThresholdGroup,
  PipelineListItemDto,
  QueryResult,
} from '@flowqa/shared';

type PipelinesTableProps = {
  persistKey?: string | null;
  syncStateToUrl?: boolean;
  pageSizeOptions?: number[];
  initialPageSize?: number;
  disablePageSizeSelect?: boolean;
  noRowsText?: string;
  fixedFilters?: GridQueryInput['filters'];
  initialSortModel?: GridSortModel;
  initialFilterModel?: GridFilterModel;
  queryFn?: (input: GridQueryInput) => Promise<QueryResult<PipelineListItemDto>>;
  queryKey?: QueryKey;
  onRowsChange?: (rows: PipelineListItemDto[]) => void;
  onQueryInput?: (input: GridQueryInput) => void;
  onDataUpdatedAt?: (timestamp: number | null) => void;
  showLastUpdated?: boolean;
  onStatusSelect?: (status: BuildStatus | 'none') => void;
  showDeltas?: boolean;
};

export const PipelinesTable = ({
  persistKey,
  syncStateToUrl = false,
  pageSizeOptions = [5, 10, 25, 50],
  initialPageSize,
  disablePageSizeSelect,
  noRowsText = 'No pipelines found',
  fixedFilters,
  initialSortModel,
  initialFilterModel,
  queryFn,
  queryKey,
  onRowsChange,
  onQueryInput,
  onDataUpdatedAt,
  showLastUpdated,
  onStatusSelect,
  showDeltas = true,
}: PipelinesTableProps): ReactElement => {
  const { session } = useAuth();
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const projectsQuery = useProjectsQuery();
  const thresholdsByProjectId = useMemo(() => {
    const entries = projectsQuery.data?.items ?? [];
    return new Map(entries.map((project) => [project.id, project.metricThresholds.pipelines]));
  }, [projectsQuery.data?.items]);
  const resolveThresholds = useMemo(
    () =>
      (row: { projectId: string }): MetricThresholdGroup | undefined =>
        thresholdsByProjectId.get(row.projectId),
    [thresholdsByProjectId],
  );
  const columns = useMemo(
    () =>
      buildPipelinesColumns({
        selectedProjectId,
        thresholds: thresholds.pipelines,
        resolveThresholds,
        onStatusSelect,
        showDeltas,
      }),
    [onStatusSelect, resolveThresholds, selectedProjectId, showDeltas, thresholds.pipelines],
  );

  const gridConfig = pipelinesGridConfig({
    organisationId: selectedOrganisationId,
    projectId: selectedProjectId,
    syncStateToUrl,
    persistKey: persistKey ?? 'pipelines-table-v2',
    fixedFilters,
    initialSortModel,
    initialFilterModel,
    includeDeltas: showDeltas,
  });
  const gridProps = buildTableGridProps<PipelineListItemDto>({
    gridConfig,
    queryFn,
    queryKey,
    pageSizeOptions,
    initialPageSize,
    disablePageSizeSelect,
    noRowsText,
    onRowsChange,
    onQueryInput,
    onDataUpdatedAt,
    showLastUpdated,
  });

  return (
    <Stack gap="md">
      <ServerDataTable<PipelineListItemDto>
        queryPath="pipelines/query"
        columns={columns}
        {...gridConfig}
        {...gridProps}
        getRowId={(row) => row.id}
        token={session?.tokens.accessToken ?? null}
        organisationId={selectedOrganisationId}
        projectId={selectedProjectId}
      />
    </Stack>
  );
};
